import React, { useState } from "react";
import ActionButton from "../../components/ui/ActionButton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { parameterUnitGroups } from "./parameterUnits";
import { getRequestErrorMessage } from "../../services/apiMessages";

const limitFields = [
  ["lower_control_limit", "Lower Control Limit"],
  ["upper_control_limit", "Upper Control Limit"],
  ["lower_specification_limit", "Lower Specification Limit"],
  ["upper_specification_limit", "Upper Specification Limit"],
];

export default function ParameterDialog({ operationLabel, onAdd, onClose, saveTarget = "item routing", initialValues }) {
  const isEdit = Boolean(initialValues);
  const [values, setValues] = useState({ ...initialValues, parameter_name: initialValues?.parameter_name || "", unit: initialValues?.unit || "", direct_parameter: initialValues?.direct_parameter ?? true });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const change = (name, value) => {
    setValues((current) => ({ ...current, [name]: value }));
    setError("");
  };
  const submit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!values.parameter_name.trim()) {
      setError("Parameter name is required.");
      return;
    }
    const parameter = {
      parameter_name: values.parameter_name.trim().toUpperCase(),
      unit: values.unit.trim() || null,
      direct_parameter: values.direct_parameter,
    };
    for (const [name, label] of limitFields) {
      const value = values[name];
      parameter[name] = value == null || value === "" ? null : Number(value);
      if (parameter[name] !== null && !Number.isFinite(parameter[name])) {
        setError(`${label} must be a valid number.`);
        return;
      }
    }
    for (const kind of ["control", "specification"]) {
      const lower = parameter[`lower_${kind}_limit`];
      const upper = parameter[`upper_${kind}_limit`];
      if (lower !== null && upper !== null && lower > upper) {
        setError(`Lower ${kind} limit must not exceed the upper ${kind} limit.`);
        return;
      }
    }
    const ordered = [parameter.lower_control_limit, parameter.lower_specification_limit,
      parameter.upper_specification_limit, parameter.upper_control_limit].filter((value) => value !== null);
    if (ordered.some((value, index) => index > 0 && value < ordered[index - 1])) {
      setError("Parameter limits must be ordered LCL ≤ LSL ≤ USL ≤ UCL.");
      return;
    }
    setIsSaving(true);
    try {
      await onAdd(parameter);
    } catch (error) {
      setError(getRequestErrorMessage(error, "Unable to update parameter."));
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !isSaving && onClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Update Parameter" : "Add Parameter"}</DialogTitle>
          <DialogDescription>For {operationLabel}. {isEdit ? "Update saves this parameter immediately." : `This parameter will be saved when you save the ${saveTarget}.`} Limits: LCL ≤ LSL ≤ USL ≤ UCL.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <fieldset disabled={isSaving} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {[["parameter_name", "Parameter Name"], ...limitFields].map(([name, label]) => (
              <label key={name} className="text-sm font-medium text-slate-700">
                {label}
                <input
                  className="mt-1.5 w-full rounded-md border border-slate-300 px-3 py-2"
                  type={name.endsWith("_limit") ? "number" : "text"}
                  step={name.endsWith("_limit") ? "any" : undefined}
                  required={name === "parameter_name"}
                  value={values[name] ?? ""}
                  onChange={(event) => change(name, event.target.value)}
                />
              </label>
            ))}
            <label className="text-sm font-medium text-slate-700">
              Unit
              <select className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3 py-2" value={values.unit} onChange={(event) => change("unit", event.target.value)}>
                <option value="">No unit</option>
                {values.unit && !parameterUnitGroups.some((group) => group.units.some(([unit]) => unit === values.unit)) && <option value={values.unit}>{values.unit}</option>}
                {parameterUnitGroups.map((group) => <optgroup key={group.label} label={group.label}>
                  {group.units.map(([value, label]) => <option key={value} value={value}>{label} ({value})</option>)}
                </optgroup>)}
              </select>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" checked={values.direct_parameter} onChange={(event) => change("direct_parameter", event.target.checked)} />
            Direct Parameter
          </label>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <ActionButton action="cancel" onClick={onClose} />
            <ActionButton action={isEdit ? "update" : "add"} label={isEdit ? "Update Parameter" : "Add Parameter"} type="submit" isLoading={isSaving} />
          </div>
          </fieldset>
        </form>
      </DialogContent>
    </Dialog>
  );
}
